import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ShippingDetail } from '../../../../../core/models/shipping-detail.model';
import { RequestsRepository } from '../../../../../infrastructure/http/repositories/requests.repository';
import { BehaviorSubject, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';

@Component({
  selector: 'app-shipping-details-modal',
  standalone: true,
  imports: [CommonModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './shipping-details-modal.component.html',
  styleUrls: ['./shipping-details-modal.component.scss'],
})
export class ShippingDetailsModalComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() requestId: number | null = null;
  @Output() closeModal = new EventEmitter<void>();

  private readonly repository = inject(RequestsRepository);

  private readonly shippingData$ = new BehaviorSubject<ShippingDetail[]>([]);
  private readonly loading$ = new BehaviorSubject<boolean>(false);
  private readonly error$ = new BehaviorSubject<string | null>(null);
  private hasDataBeenLoaded = false;

  readonly shippingData = this.shippingData$.asObservable();
  readonly loading = this.loading$.asObservable();
  readonly error = this.error$.asObservable();

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['isOpen'] &&
      this.isOpen &&
      !this.hasDataBeenLoaded &&
      this.requestId
    ) {
      this.loadShippingDetails();
      this.hasDataBeenLoaded = true;
    }
    if (
      changes['requestId'] &&
      !changes['requestId'].firstChange &&
      this.isOpen &&
      this.hasDataBeenLoaded &&
      changes['requestId'].currentValue
    ) {
      this.loadShippingDetails();
    }
  }

  private loadShippingDetails(): void {
    if (!this.requestId) {
      return;
    }

    this.loading$.next(true);
    this.error$.next(null);

    this.repository
      .updateShippingDetails(this.requestId)
      .pipe(
        tap(data => {
          this.shippingData$.next(data);
          this.loading$.next(false);
        }),
        catchError(() => {
          this.loading$.next(false);
          this.error$.next('Failed to load shipping details');
          return of([]);
        })
      )
      .subscribe();
  }

  onClose(): void {
    this.closeModal.emit();
  }

  onOverlayClick(event: MouseEvent): void {
    // Close modal when clicking on the overlay (not the modal content)
    if ((event.target as HTMLElement).classList.contains('modal-overlay')) {
      this.closeModal.emit();
    }
  }

  formatCurrency(amount: number): string {
    return `$${amount.toFixed(2)}`;
  }

  formatDate(date: Date | null): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  getStatusLabel(status: string): string {
    return status;
  }

  getExpeditedYesLabel(): string {
    return 'Yes';
  }

  getExpeditedNoLabel(): string {
    return 'No';
  }
}
